/**
 * Vera's own support address.
 *
 * Extracted on its THIRD hand-roll (working-conventions §36 — one is allowed,
 * the second is a shared thing): `pages/Signup` wrote it twice and
 * `pages/SignupSuccess` once, each as a literal inside a `mailto:` and again as
 * the link text, so changing the address meant finding six strings.
 */
export const VERA_SUPPORT_EMAIL = 'hello@vera.inc'

/**
 * Where a report about a BROKEN PAGE goes — Vera, never the brand.
 *
 * ## The decision, because the recommendation on file assumed the opposite
 *
 * The open proposal was to route the error page's support link to the brand's
 * own `support_email` (the address `companies` already carries, surfaced in
 * `company > general`) and fall back to Vera. That is the right split on the
 * surfaces that already do it — `SealOpenedDrawer` sends a tampered-seal
 * question to the brand, because a tampered seal is a question about the
 * PRODUCT and the ORDER, which is genuinely the brand's business and which only
 * they can answer.
 *
 * A render crash is not that. It is a defect in Vera's own JavaScript, and
 * routing it to the tenant's support desk is wrong in both directions: the
 * brand's team cannot reproduce it, cannot fix it, does not own the code, and
 * will forward it to Vera anyway — so the tenant absorbs support load for
 * someone else's bug, and the people who CAN fix it hear about it later, or
 * never. "Whoever can act on it" is the routing rule, and for a stack trace
 * that is only ever Vera.
 *
 * The fallback half of the proposal is therefore moot: there is nothing to fall
 * back FROM. It also removes what would otherwise have been the blocking
 * dependency — `support_email` is not on any endpoint the app holds at crash
 * time (`/companies/public/:id/settings/style` and `.../by-subdomain/:sub` both
 * return `styles` and nothing else; the field reaches the app only nested inside
 * a loaded certificate, which a crash may well have happened before). Routing to
 * Vera needs no API change and no new failure mode.
 */
export const ERROR_REPORT_EMAIL = VERA_SUPPORT_EMAIL

/**
 * Build the `mailto:` for a crash report.
 *
 * ## What is deliberately NOT in the body
 *
 * The error MESSAGE and the STACK. `ErrorBoundary` already renders those only
 * under `import.meta.env.DEV`, on the stated grounds that "an error message can
 * carry ids, tokens or internal paths" — and a mailto body is shown to the user
 * in their compose window, so putting them there would defeat that guard by a
 * side door rather than respect it. The full detail still reaches
 * `console.error` on the user's own machine, where a developer sitting with them
 * can read it.
 *
 * What IS included is the part that is both safe and sufficient to correlate: the
 * error's CONSTRUCTOR NAME (`TypeError`, `ReferenceError` — a type, carrying no
 * values), the path, the time, and the user agent. Plus a prompt for the one
 * thing no automatic capture can supply, which is what the person was doing.
 *
 * ⚠️ This email is currently the ONLY channel a crash has. There is no error
 * sink — no Sentry, no telemetry endpoint, nothing server-side — so a crash that
 * the user does not choose to write in about is invisible to Vera. The proposal's
 * "plus automatic error reporting" half is therefore NOT implemented here rather
 * than half-implemented: it needs a real endpoint, which is its own piece of
 * work.
 *
 * TODO(VRG-382 — App.Error.Reporting.Sink): POST the crash on `componentDidCatch`
 * so a report does not depend on the user choosing to write one.
 */
export const buildErrorReportMailto = (error: Error | null): string => {
  const subject = 'Vera app — something went wrong'

  const body = [
    'Something went wrong on a page in the Vera app.',
    '',
    'What I was doing when it happened:',
    '',
    '',
    '---',
    `Page: ${window.location.href}`,
    `Type: ${error?.name || 'Unknown'}`,
    `Time: ${new Date().toISOString()}`,
    `Browser: ${navigator.userAgent}`,
  ].join('\n')

  return `mailto:${ERROR_REPORT_EMAIL}?subject=${encodeURIComponent(
    subject,
  )}&body=${encodeURIComponent(body)}`
}

/**
 * Build the `mailto:` for a FAILED TAG SCAN — the `pages/SomethingWentWrong` link.
 *
 * Sibling of `buildErrorReportMailto` above, and deliberately a second function rather
 * than a widened first one. They report different events to the same desk: that one is a
 * CLIENT render crash and keys on an `Error` object it can name the constructor of; this
 * one is a SERVER error during `GET /verify/:global_tag_id`, where the client holds no
 * error at all — the failure happened before this app was running, and the only handle on
 * it is the correlation id the API chose to pass. Merging them would mean one signature
 * with two mutually-exclusive halves and a subject line that has to guess which happened.
 *
 * It goes to VERA, not the brand, for exactly the reason set out on `ERROR_REPORT_EMAIL`:
 * "whoever can act on it" is the routing rule, and a 500 in Vera's own verify endpoint is
 * a thing only Vera can fix. The brand's desk cannot reproduce it, cannot fix it, and does
 * not own the code. Note this is the OPPOSITE of the rule the neighbouring state pages
 * follow (`TagReplaced`, `CertificateNotActive` point at the brand and never name Vera) —
 * and correctly so: those pages are about the ITEM's state, which is genuinely the brand's
 * business, whereas this one is about VERA being broken.
 *
 * ## The `ref`
 *
 * `ref` is the API's correlation id, present only when it had one, and it is the whole
 * point of the link: it is what turns "a customer says a tap failed" into a line the
 * server logs can be searched for. So it is put in the SUBJECT as well as the body —
 * subjects survive forwarding, quoting and ticket-system munging that eats a body.
 *
 * When it is absent the link must still work, so the id is simply omitted: no "Reference:"
 * with nothing after it, and no empty `ref=` anywhere. The report is worth less without it
 * but is still worth having, since the timestamp plus the page URL narrow the logs.
 *
 * ⚠️ Same standing gap as the crash reporter: there is no error sink in this app, so a
 * scan failure the customer does not choose to write in about is invisible to Vera. The
 * API's own server-side logging is the other half here, which the crash path does not
 * have — hence `ref` mattering so much.
 * TODO(VRG-382 — App.Error.Reporting.Sink): report this automatically rather than
 * depending on a customer who has just had a bad experience choosing to write an email.
 */
export const buildScanErrorReportMailto = (ref?: string | null): string => {
  const trimmedRef = ref?.trim()

  const subject = trimmedRef
    ? `Vera — tag scan failed (ref ${trimmedRef})`
    : 'Vera — tag scan failed'

  const body = [
    'Something went wrong when I tapped a Vera tag.',
    '',
    'What I was doing when it happened:',
    '',
    '',
    '---',
    // Only when we have one. An empty "Reference:" reads as a second thing that failed.
    ...(trimmedRef ? [`Reference: ${trimmedRef}`] : []),
    `Page: ${window.location.href}`,
    `Time: ${new Date().toISOString()}`,
    `Browser: ${navigator.userAgent}`,
  ].join('\n')

  return `mailto:${ERROR_REPORT_EMAIL}?subject=${encodeURIComponent(
    subject,
  )}&body=${encodeURIComponent(body)}`
}
